import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, rupiah, slugify } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { SearchBar } from "@/components/SearchBar";
import { InstallPWA } from "@/components/InstallPWA";
import { DepartureBoard } from "@/components/DepartureBoard";
import { BookingDialog } from "@/components/BookingDialog";
import { Badge } from "@/components/ui/badge";
import { DriverBadges } from "@/components/DriverBadges";
import { ShieldCheck, Star, Zap, Search, ClipboardCheck, MessageCircle, ArrowRight, Bus, Percent, Clock, CreditCard, Headphones, Quote, Route as RouteIcon, MapPinned, Gift, MapPin, Tag, Package, Info, Truck, Wallet } from "lucide-react";
import { Seo } from "@/components/Seo";

export default function Home() {
  const [terminal, setTerminal] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [cities, setCities] = useState([]);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    api.get("/schedules/terminal").then((r) => setTerminal(r.data)).catch(() => {});
    api.get("/routes/popular").then((r) => setRoutes(r.data)).catch(() => {});
    api.get("/drivers/top").then((r) => setDrivers(r.data)).catch(() => {});
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Seo
        path="/"
        description="Bandingkan jadwal, harga tiket, armada, dan sisa kursi travel door to door & charter drop off antarkota dari banyak driver dalam satu Digital Terminal. Pesan mudah lewat WhatsApp Admin Travele."
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Travel Door to Door & Charter Drop Off",
          provider: { "@type": "Organization", name: "Travele" },
          areaServed: "Indonesia",
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/36681344/pexels-photo-36681344.jpeg?auto=compress&cs=tinysrgb&w=1920&q=60)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <Badge className="tv-reveal tv-delay-1 mb-4 bg-primary/15 font-mono-terminal text-primary hover:bg-primary/15" data-testid="hero-badge">
            <Zap className="mr-1 h-3 w-3" /> DIGITAL TERMINAL INDONESIA
          </Badge>
          <h1 className="tv-reveal tv-delay-2 max-w-3xl font-display text-4xl font-black uppercase tracking-tighter sm:text-5xl lg:text-6xl">
            Papan Jadwal Travel <span className="text-primary">Door to Door</span> & Charter Drop Off
          </h1>
          <p className="tv-reveal tv-delay-3 mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Bandingkan jadwal keberangkatan, harga tiket, armada, dan sisa kursi dari banyak driver dalam satu terminal digital. Pesan mudah lewat Admin Travele.
          </p>
          <div id="cari" className="tv-reveal tv-delay-4 mt-8 max-w-4xl scroll-mt-24">
            <SearchBar />
          </div>
          <div className="tv-reveal tv-delay-4 mt-5 flex max-w-4xl items-center gap-2.5 rounded-lg border border-[#25D366]/30 bg-[#25D366]/10 px-4 py-3" data-testid="hero-payment-note">
            <Wallet className="h-5 w-5 shrink-0 text-[#25D366]" />
            <p className="text-base font-semibold text-foreground sm:text-lg">
              Pembayaran bisa langsung ke driver saat sudah dijemput.
            </p>
          </div>
          {routes.length > 0 && (
            <div className="tv-reveal tv-delay-5 mt-4 flex flex-wrap items-center gap-2" data-testid="hero-popular-routes">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Rute populer:</span>
              {routes.slice(0, 5).map((r) => (
                <Link key={r.slug} to={`/${r.slug}`} data-testid={`hero-route-${r.slug}`}
                  className="rounded-full border border-border bg-card/70 px-3 py-1 text-xs text-foreground backdrop-blur transition-colors hover:border-primary hover:text-primary">
                  {r.origin} → {r.destination}
                </Link>
              ))}
            </div>
          )}
          <InstallPWA />
          <div className="mt-6 flex flex-wrap gap-6 text-sm text-muted-foreground">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Driver Terverifikasi</span>
            <span className="flex items-center gap-2"><Percent className="h-4 w-4 text-primary" /> Harga oleh Driver</span>
            <span className="flex items-center gap-2"><MessageCircle className="h-4 w-4 text-[#25D366]" /> Pemesanan via Admin</span>
          </div>
        </div>
      </section>

      {/* marquee */}
      <div className="overflow-hidden border-b border-border bg-black py-3">
        <div className="animate-marquee flex w-max gap-8 whitespace-nowrap font-mono-terminal text-sm text-muted-foreground">
          {[...routes, ...routes].map((r, i) => (
            <span key={i} className="flex items-center gap-2">
              <span className="text-primary">▸</span> {r.origin} → {r.destination} · mulai {rupiah(r.min_price)}
            </span>
          ))}
          {routes.length === 0 && <span>SEMARANG → JAKARTA · SEMARANG → BANDUNG · KUDUS → JAKARTA · JEPARA → BEKASI</span>}
        </div>
      </div>

      {/* Layanan */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="mb-8 max-w-2xl">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Pilih Layanan Perjalanan Anda</h2>
          <p className="mt-1 text-sm text-muted-foreground">Dua cara nyaman bepergian antarkota bersama Travele.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="group relative overflow-hidden border border-border bg-card p-8 transition-colors hover:border-primary" data-testid="layanan-door">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-primary/15 text-primary">
              <MapPinned className="h-6 w-6" />
            </div>
            <h3 className="font-display text-xl font-bold">Travel Door to Door</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Dijemput langsung dari rumah dan diantar sampai tujuan. Tanpa repot ke pool, cocok untuk perjalanan santai bersama keluarga.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Jemput & antar sesuai alamat</li>
              <li className="flex items-center gap-2"><Star className="h-4 w-4 text-primary" /> Armada nyaman & ber-AC</li>
              <li className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Banyak pilihan jam berangkat</li>
            </ul>
            <Link to="/pesan-travel" data-testid="layanan-door-cta">
              <button className="mt-5 flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 font-bold text-primary-foreground transition-transform hover:-translate-y-0.5">
                <MapPinned className="h-4 w-4" /> Pesan Travel Door to Door
              </button>
            </Link>
          </div>
          <div className="group relative overflow-hidden border border-border bg-card p-8 transition-colors hover:border-primary" data-testid="layanan-charter">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-primary/15 text-primary">
              <RouteIcon className="h-6 w-6" />
            </div>
            <h3 className="font-display text-xl font-bold">Charter Drop Off</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Sewa satu kendaraan penuh untuk perjalanan pribadi. Berangkat kapan saja, rute fleksibel, privasi terjaga untuk rombongan Anda.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Satu mobil untuk Anda sendiri</li>
              <li className="flex items-center gap-2"><Star className="h-4 w-4 text-primary" /> Bebas atur titik jemput</li>
              <li className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Cocok untuk rombongan & acara</li>
            </ul>
            <Link to="/pesan-charter" data-testid="layanan-charter-cta">
              <button className="mt-5 flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 font-bold text-primary-foreground transition-transform hover:-translate-y-0.5">
                <RouteIcon className="h-4 w-4" /> Pesan Charter Mobil
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Digital Terminal */}
      <section id="terminal" className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Digital Terminal — Keberangkatan Terkini</h2>
            <p className="mt-1 text-sm text-muted-foreground">Jadwal aktif dari seluruh driver, seperti papan jadwal bandara.</p>
          </div>
        </div>

        <div className="mb-6 border border-primary/25 bg-primary/[0.06] p-4 sm:p-5" data-testid="terminal-estimate-note">
          <div className="mb-2 flex items-center gap-2 font-display text-base font-bold">
            <Info className="h-4 w-4 text-primary" /> Estimasi Jadwal Keberangkatan
          </div>
          <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
            <p>Seluruh jadwal keberangkatan yang ditampilkan pada sistem merupakan <b className="text-foreground">estimasi waktu keberangkatan</b>, bukan waktu yang bersifat pasti.</p>
            <p>Layanan Travele menggunakan sistem <b className="text-foreground">Travel Door to Door</b>, sehingga setiap armada harus melakukan proses penjemputan penumpang di beberapa lokasi sebelum memulai perjalanan menuju kota tujuan. Oleh karena itu, kondisi lalu lintas, jarak antar lokasi penjemputan, cuaca, maupun situasi di lapangan dapat menyebabkan waktu keberangkatan menjadi lebih cepat atau lebih lambat dari jadwal yang tertera.</p>
            <p>Calon penumpang diharapkan memahami bahwa selisih waktu keberangkatan merupakan hal yang wajar dalam operasional layanan Door to Door.</p>
          </div>
        </div>

        <DepartureBoard schedules={terminal} onBook={setBooking} />

        <div className="mt-6 border border-border bg-card p-4 sm:p-5" data-testid="terminal-langsir-note">
          <div className="mb-2 flex items-center gap-2 font-display text-base font-bold">
            <Truck className="h-4 w-4 text-primary" /> Tim Langsir — Penjemputan & Pengantaran Lebih Cepat
          </div>
          <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
            <p>Untuk memberikan pelayanan yang lebih cepat dan efisien, Travele juga memiliki <b className="text-foreground">Tim Langsir</b>. Tim ini bertugas membantu proses penjemputan maupun pengantaran penumpang menggunakan armada lain apabila diperlukan, sehingga waktu tunggu dapat diminimalkan dan perjalanan menjadi lebih efektif.</p>
            <p>Seluruh biaya operasional Tim Langsir menjadi <b className="text-foreground">tanggung jawab driver atau mitra travel</b> dan tidak akan menambah biaya perjalanan yang telah disepakati dengan penumpang pada saat pemesanan. Penumpang tetap membayar sesuai harga yang telah dikonfirmasi di awal tanpa biaya tambahan akibat proses langsir.</p>
            <p>Dengan melakukan pemesanan melalui Travele, penumpang dianggap telah memahami dan menyetujui ketentuan mengenai estimasi waktu keberangkatan serta mekanisme operasional layanan Door to Door ini.</p>
          </div>
        </div>
      </section>

      {/* Paket Kilat */}
      <section className="border-y border-border bg-primary/5">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 font-mono-terminal text-xs uppercase tracking-widest text-primary">
                <Zap className="h-3.5 w-3.5" /> Layanan Baru
              </div>
              <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">Kirim Paket Kilat — Barang & Dokumen</h2>
              <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                Titip dokumen penting atau paket barang antarkota lewat driver Travele. Penjemputan door to door, cepat, dan aman. Tarif dikonfirmasi Admin.
              </p>
            </div>
            <Link to="/kirim-paket" data-testid="home-package-cta">
              <button className="flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-bold text-primary-foreground transition-transform hover:-translate-y-0.5">
                <Package className="h-5 w-5" /> Kirim Paket Sekarang
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Rute populer */}
      <section id="rute" className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <h2 className="mb-6 font-display text-2xl font-bold tracking-tight sm:text-3xl">Rute Populer</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {routes.map((r) => (
              <Link
                key={r.slug}
                to={`/${r.slug}`}
                data-testid={`route-card-${r.slug}`}
                className="group border border-border bg-background p-5 transition-colors hover:border-primary"
              >
                <div className="flex items-center justify-between font-mono-terminal text-xs uppercase text-muted-foreground">
                  <span>{r.service_type === "charter" ? "Charter" : "Door to Door"}</span>
                  <ArrowRight className="h-4 w-4 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
                <div className="mt-2 font-display text-lg font-bold">{r.origin} → {r.destination}</div>
                <div className="mt-1 text-sm text-muted-foreground">Mulai {rupiah(r.min_price)} · {r.count} jadwal</div>
              </Link>
            ))}
            {routes.length === 0 && <p className="text-muted-foreground">Belum ada rute.</p>}
          </div>
        </div>
      </section>

      {/* Cara pesan */}
      <section id="cara" className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <h2 className="mb-8 font-display text-2xl font-bold tracking-tight sm:text-3xl">Cara Pemesanan</h2>
        <div className="grid gap-4 md:grid-cols-4">
          {[
            { icon: Search, t: "Cari Rute", d: "Pilih asal, tujuan, tanggal, dan layanan." },
            { icon: ClipboardCheck, t: "Pilih Jadwal", d: "Bandingkan harga, armada, dan sisa kursi." },
            { icon: MessageCircle, t: "Pesan via Admin", d: "Order otomatis diteruskan ke WhatsApp Admin." },
            { icon: Bus, t: "Berangkat", d: "Driver menjemput, lalu beri rating." },
          ].map((s, i) => (
            <div key={i} className="border border-border bg-card p-6">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-primary/15 text-primary">
                <s.icon className="h-5 w-5" />
              </div>
              <div className="font-mono-terminal text-xs text-primary">0{i + 1}</div>
              <h3 className="mt-1 font-display text-lg font-bold">{s.t}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Kota Layanan */}
      <section className="border-t border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <div className="mb-8 max-w-2xl">
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Kota Layanan Travele</h2>
            <p className="mt-1 text-sm text-muted-foreground">Jelajahi travel door to door dari kota-kota berikut di seluruh Indonesia.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/travel-dari-${c.slug}`}
                data-testid={`kota-${c.slug}`}
                className="group flex items-center gap-2 border border-border bg-background px-3 py-3 transition-colors hover:border-primary"
              >
                <MapPin className="h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">{c.name}</div>
                  <div className="truncate font-mono-terminal text-[10px] uppercase text-muted-foreground">{c.province}</div>
                </div>
              </Link>
            ))}
            {cities.length === 0 && <p className="text-muted-foreground">Memuat kota...</p>}
          </div>
        </div>
      </section>

      {/* Top drivers */}
      {drivers.length > 0 && (
        <section className="border-t border-border bg-card">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
            <h2 className="mb-6 font-display text-2xl font-bold tracking-tight sm:text-3xl">Driver Rating Terbaik</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {drivers.map((d) => (
                <Link key={d.id} to={`/driver/${slugify(d.business_name || d.name)}`} className="flex items-center gap-3 border border-border bg-background p-4 transition-colors hover:border-primary" data-testid={`top-driver-${d.id}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary/15 font-display font-bold text-primary">
                    {(d.business_name || d.name).slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-1 font-semibold">
                      {d.business_name || d.name}
                      {d.verified && <ShieldCheck className="h-4 w-4 text-emerald-400" />}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Star className="h-3.5 w-3.5 fill-primary text-primary" /> {d.avg_rating || "Baru"}
                      {d.rating_count > 0 && <span className="text-xs">({d.rating_count})</span>} · {d.city}
                    </div>
                    <DriverBadges size="xs" verified={d.verified} priority={d.priority} rating={d.avg_rating} reviews={d.rating_count} trips={d.total_trips} testId={`top-driver-badges-${d.id}`} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Promo */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="mb-8 flex items-end justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Promo & Penawaran</h2>
            <p className="mt-1 text-sm text-muted-foreground">Hemat lebih banyak untuk perjalanan berikutnya.</p>
          </div>
          <Badge className="bg-primary/15 font-mono-terminal text-primary hover:bg-primary/15">
            <Tag className="mr-1 h-3 w-3" /> TERBATAS
          </Badge>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { code: "TraveleBARU", t: "Diskon Penumpang Baru", d: "Potongan Rp25.000 untuk order pertama via Admin Travele.", accent: true },
            { code: "PP2ARAH", t: "Hemat Pulang-Pergi", d: "Pesan tiket dua arah dan dapatkan harga spesial dari driver pilihan." },
            { code: "ROMBONGAN", t: "Diskon Rombongan", d: "Perjalanan 4 penumpang atau lebih? Tanyakan harga rombongan ke Admin." },
          ].map((p, i) => (
            <div
              key={i}
              data-testid={`promo-${i}`}
              className={`relative overflow-hidden border p-6 transition-transform hover:-translate-y-1 ${p.accent ? "border-primary/50 bg-primary/5" : "border-border bg-card"}`}
            >
              <Gift className={`h-7 w-7 ${p.accent ? "text-primary" : "text-muted-foreground"}`} />
              <h3 className="mt-3 font-display text-lg font-bold">{p.t}</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{p.d}</p>
              <div className="mt-4 flex items-center gap-2">
                <span className="border border-dashed border-primary/60 bg-background px-3 py-1 font-mono-terminal text-sm font-bold text-primary">{p.code}</span>
                <span className="text-xs text-muted-foreground">Sebutkan kode ke Admin</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Keunggulan */}
      <section id="keunggulan" className="border-t border-border bg-card">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <div className="mb-8 max-w-2xl">
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Kenapa Pesan di Travele?</h2>
            <p className="mt-1 text-sm text-muted-foreground">Nyaman, transparan, dan aman untuk setiap perjalanan Anda.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: ClipboardCheck, t: "Semua Jadwal Satu Layar", d: "Bandingkan harga, armada, jam, dan sisa kursi dari banyak travel sekaligus." },
              { icon: ShieldCheck, t: "Driver Terverifikasi", d: "Setiap driver diverifikasi Admin, lengkap dengan rating dari penumpang asli." },
              { icon: CreditCard, t: "Harga Transparan", d: "Harga tiket ditampilkan jelas di awal. Tanpa biaya tersembunyi." },
              { icon: Headphones, t: "Dibantu Admin", d: "Satu nomor WhatsApp resmi membantu pesanan Anda dari awal hingga selesai." },
            ].map((f, i) => (
              <div key={i} className="border border-border bg-background p-6" data-testid={`keunggulan-${i}`}>
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-primary/15 text-primary">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-lg font-bold">{f.t}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ulasan Penumpang */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="mb-8 max-w-2xl">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Kata Penumpang</h2>
          <p className="mt-1 text-sm text-muted-foreground">Pengalaman nyata dari mereka yang sudah berangkat bersama Travele.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { n: "Dewi A.", r: "Semarang → Jakarta", c: "Dijemput tepat waktu di depan rumah, mobilnya bersih dan driver ramah. Pesan lewat WhatsApp Admin juga gampang banget." },
            { n: "Rizal M.", r: "Kudus → Jakarta", c: "Harga jelas dari awal, tinggal pilih jam yang pas. Nggak perlu telepon sana-sini, semua diurus Admin." },
            { n: "Siti R.", r: "Jepara → Bekasi", c: "Bisa bandingkan banyak travel dalam satu halaman. Praktis untuk yang sering pulang kampung seperti saya." },
          ].map((t, i) => (
            <div key={i} className="border border-border bg-card p-6" data-testid={`ulasan-${i}`}>
              <Quote className="h-6 w-6 text-primary/50" />
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">"{t.c}"</p>
              <div className="mt-4 flex items-center gap-1 text-primary">
                {[...Array(5)].map((_, s) => <Star key={s} className="h-4 w-4 fill-primary" />)}
              </div>
              <div className="mt-2 text-sm font-semibold">{t.n}</div>
              <div className="font-mono-terminal text-xs text-muted-foreground">{t.r}</div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
      <BookingDialog schedule={booking} open={!!booking} onClose={() => setBooking(null)} />
    </div>
  );
}
